/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { marked } from "marked";
import type { Token, Tokens } from "marked";
import type { Content } from "pdfmake/interfaces";
import {
  CODE_BLOCK_LAYOUT,
  COLORS,
  CONTENT_WIDTH,
  MAX_IMAGE_HEIGHT,
  PX_TO_PT,
  TABLE_LAYOUT,
} from "./PdfStyles";

/**
 * Translates the documentation markdown into pdfmake content nodes.
 *
 * pdfmake has no markdown support, so each token emitted by marked's lexer is
 * mapped explicitly. Anything unrecognised is skipped rather than throwing, so
 * a new markdown construct degrades to missing content rather than a failed
 * download — the character coverage check below is the guard that surfaces such
 * gaps during development.
 */

/** Source URL to base64 data URL. */
export type ImageMap = ReadonlyMap<string, string>;

/**
 * Characters that must be rewritten before they reach the PDF.
 *
 * pdfmake performs no font fallback: a glyph missing from the embedded face is
 * drawn as blank space or a .notdef box. The bundled Roboto covers Latin text
 * but not emoji or the less common symbols used in the documentation, so those
 * are replaced with ASCII equivalents that carry the same meaning.
 *
 * Editing the markdown source to avoid these characters is the cleaner fix;
 * these substitutions exist so the PDF is correct in the meantime.
 */
export const GLYPH_SUBSTITUTIONS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\u22EE/g, "..."], // vertical ellipsis, used for overflow menus
  [/\u2192/g, "->"], // rightwards arrow
  [/\u2190/g, "<-"], // leftwards arrow
  [/\t/g, "    "], // tabs render inconsistently; normalise to spaces
];

/**
 * Characters known to render correctly with the bundled font: printable ASCII,
 * Latin-1 supplement, and the handful of typographic marks the documentation
 * uses.
 */
const SUPPORTED_CHARACTERS =
  /[\u0020-\u007E\u00A0-\u00FF\u2018\u2019\u201C\u201D\u2013\u2014\u2026\n\r]/;

/**
 * Reports characters in the supplied markdown that neither render reliably nor
 * have a substitution defined above.
 *
 * Intended for a unit test over `docSections`, so that content added later
 * fails loudly instead of producing blank boxes in a delivered document.
 */
export const findUnsupportedCharacters = (
  markdown: string
): readonly string[] => {
  const unsupported = new Set<string>();

  Array.from(applyGlyphSubstitutions(markdown)).forEach((character) => {
    if (!SUPPORTED_CHARACTERS.test(character)) {
      unsupported.add(character);
    }
  });

  return Array.from(unsupported);
};

const applyGlyphSubstitutions = (text: string): string =>
  GLYPH_SUBSTITUTIONS.reduce(
    (result, [pattern, replacement]) => result.replace(pattern, replacement),
    text
  );

/**
 * Decodes HTML entities that survive lexing (`&amp;` in the "Transform Families
 * & Types" heading, for example). A textarea's `value` performs the decode
 * without evaluating the markup.
 */
let entityDecoder: HTMLTextAreaElement | undefined;
const decodeEntities = (text: string): string => {
  if (!text.includes("&")) {
    return text;
  }
  entityDecoder = entityDecoder ?? document.createElement("textarea");
  entityDecoder.innerHTML = text;
  return entityDecoder.value;
};

const normalizeText = (text: string): string =>
  applyGlyphSubstitutions(decodeEntities(text));

/* -------------------------------------------------------------------------- */
/* Lexing                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Lexes markdown into tokens.
 *
 * Options are passed explicitly rather than relying on `marked.setOptions`, so
 * this path is unaffected by global configuration applied elsewhere in the
 * application.
 */
export const lexMarkdown = (markdown: string): Token[] =>
  marked.lexer(markdown, { gfm: true, breaks: false });

interface NestedToken {
  tokens?: Token[];
  items?: Token[];
}

const walkTokens = (tokens: Token[], visit: (token: Token) => void): void => {
  tokens.forEach((token) => {
    visit(token);
    const nested = token as NestedToken;
    if (nested.tokens) {
      walkTokens(nested.tokens, visit);
    }
    if (nested.items) {
      walkTokens(nested.items, visit);
    }
  });
};

/**
 * Collects every image source referenced by the supplied tokens, so all
 * screenshots can be fetched in parallel before rendering begins.
 */
export const collectImageSources = (tokens: Token[]): string[] => {
  const sources: string[] = [];

  walkTokens(tokens, (token) => {
    if (token.type !== "html") {
      return;
    }
    parseImageElements((token as Tokens.HTML).text).forEach((image) => {
      if (image.src) {
        sources.push(image.src);
      }
    });
  });

  return sources;
};

/* -------------------------------------------------------------------------- */
/* Raw HTML images                                                             */
/* -------------------------------------------------------------------------- */

interface ParsedImage {
  src: string;
  /** Width in points, derived from the element's `max-width`. */
  width: number;
}

/**
 * Extracts `<img>` elements from a raw HTML token.
 *
 * The documentation embeds screenshots as literal `<img>` tags, which marked
 * emits as opaque HTML. DOMParser is used rather than a regular expression
 * because it handles the several places where consecutive images share a single
 * token; parsing as `text/html` does not execute scripts or fetch resources.
 */
const parseImageElements = (html: string): ParsedImage[] => {
  const document_ = new DOMParser().parseFromString(html, "text/html");

  return Array.from(document_.querySelectorAll("img")).map((element) => ({
    src: element.getAttribute("src") ?? "",
    width: imageWidthFor(element),
  }));
};

/**
 * Honours the `max-width` the author set on the image, converted to points and
 * capped at the printable width of the page.
 */
const imageWidthFor = (element: HTMLImageElement): number => {
  const maxWidthPixels = Number.parseFloat(element.style.maxWidth);
  if (!Number.isFinite(maxWidthPixels) || maxWidthPixels <= 0) {
    return CONTENT_WIDTH;
  }
  return Math.min(maxWidthPixels * PX_TO_PT, CONTENT_WIDTH);
};

const renderImages = (html: string, images: ImageMap): Content[] =>
  parseImageElements(html).flatMap<Content>((image) => {
    const dataUrl = image.src ? images.get(image.src) : undefined;
    if (dataUrl === undefined) {
      // Unresolved screenshot; omit it rather than fail the whole document.
      return [];
    }
    return [
      {
        image: dataUrl,
        // `fit` scales down to the box without ever enlarging, which keeps
        // small node screenshots crisp and forces tall ones onto one page.
        fit: [image.width, MAX_IMAGE_HEIGHT],
        alignment: "center",
        margin: [0, 8, 0, 12],
      },
    ];
  });

/* -------------------------------------------------------------------------- */
/* Inline content                                                              */
/* -------------------------------------------------------------------------- */

interface InlineMarks {
  bold?: boolean;
  italics?: boolean;
  decoration?: "lineThrough";
}

const renderInline = (
  tokens: Token[] | undefined,
  marks: InlineMarks = {}
): Content[] =>
  (tokens ?? []).flatMap((token) => renderInlineToken(token, marks));

const renderInlineToken = (token: Token, marks: InlineMarks): Content[] => {
  switch (token.type) {
    case "text": {
      const text = token as Tokens.Text;
      // Text tokens inside list items carry their own inline children.
      return text.tokens
        ? renderInline(text.tokens, marks)
        : [{ text: normalizeText(text.text), ...marks }];
    }
    case "escape":
      return [{ text: normalizeText((token as Tokens.Escape).text), ...marks }];
    case "strong":
      return renderInline((token as Tokens.Strong).tokens, {
        ...marks,
        bold: true,
      });
    case "em":
      return renderInline((token as Tokens.Em).tokens, {
        ...marks,
        italics: true,
      });
    case "del":
      return renderInline((token as Tokens.Del).tokens, {
        ...marks,
        decoration: "lineThrough",
      });
    case "codespan":
      return [
        {
          text: normalizeText((token as Tokens.Codespan).text),
          style: "inlineCode",
          ...marks,
        },
      ];
    case "link": {
      const link = token as Tokens.Link;
      return [
        {
          text: renderInline(link.tokens, marks),
          link: link.href,
          style: "link",
        },
      ];
    }
    case "br":
      return [{ text: "\n" }];
    default:
      // Inline images and raw inline HTML are not used by the documentation.
      return [];
  }
};

/* -------------------------------------------------------------------------- */
/* Block content                                                               */
/* -------------------------------------------------------------------------- */

export const renderBlockTokens = (
  tokens: Token[],
  images: ImageMap
): Content[] => tokens.flatMap((token) => renderBlockToken(token, images));

const renderBlockToken = (token: Token, images: ImageMap): Content[] => {
  switch (token.type) {
    case "heading":
      return [renderHeading(token as Tokens.Heading)];

    case "paragraph":
      return [
        {
          text: renderInline((token as Tokens.Paragraph).tokens),
          style: "paragraph",
        },
      ];

    case "text": {
      const text = token as Tokens.Text;
      return [
        {
          text: text.tokens
            ? renderInline(text.tokens)
            : normalizeText(text.text),
          style: "paragraph",
        },
      ];
    }

    case "code":
      return [renderCodeBlock(token as Tokens.Code)];

    case "list":
      return [renderList(token as Tokens.List, images)];

    case "table":
      return [renderTable(token as Tokens.Table)];

    case "blockquote":
      return [
        {
          stack: renderBlockTokens((token as Tokens.Blockquote).tokens, images),
          italics: true,
          margin: [16, 0, 0, 0],
        },
      ];

    case "hr":
      return [
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 0,
              x2: CONTENT_WIDTH,
              y2: 0,
              lineWidth: 0.5,
              lineColor: COLORS.rule,
            },
          ],
          margin: [0, 10, 0, 16],
        },
      ];

    case "html":
      return renderImages((token as Tokens.HTML).text, images);

    default:
      // `space`, `def` and anything unrecognised contribute nothing.
      return [];
  }
};

/**
 * Section titles are rendered as h1 by the document builder, so markdown
 * headings are clamped to h2..h4. Only h2 headings become table of contents
 * entries; including every h3 would run the contents to several pages.
 */
const renderHeading = (heading: Tokens.Heading): Content => {
  const depth = Math.min(Math.max(heading.depth, 2), 4);
  const isTocEntry = depth === 2;

  return {
    text: renderInline(heading.tokens),
    style: `h${depth}`,
    ...(isTocEntry ? { tocItem: true, tocMargin: [16, 0, 0, 0] } : {}),
  };
};

/**
 * pdfmake cannot fill the background of a plain text node, so code blocks are
 * wrapped in a single-cell borderless table with a fill colour.
 */
const renderCodeBlock = (code: Tokens.Code): Content => ({
  table: {
    widths: ["*"],
    body: [[{ text: normalizeText(code.text), style: "code" }]],
  },
  layout: CODE_BLOCK_LAYOUT,
  margin: [0, 4, 0, 12],
});

const renderList = (list: Tokens.List, images: ImageMap): Content => {
  const items = list.items.map<Content>((item) => {
    const content = renderBlockTokens(item.tokens, images);
    // Collapse the common single-paragraph case so nested lists stay readable.
    return content.length === 1 ? content[0] : { stack: content };
  });

  if (!list.ordered) {
    return { ul: items, style: "list" };
  }

  return {
    ol: items,
    style: "list",
    ...(typeof list.start === "number" && list.start !== 1
      ? { start: list.start }
      : {}),
  };
};

const renderTable = (table: Tokens.Table): Content => {
  const header = table.header.map<Content>((cell) => ({
    text: renderInline(cell.tokens),
    style: "tableHeader",
  }));

  const rows = table.rows.map((row) =>
    row.map<Content>((cell) => ({
      text: renderInline(cell.tokens),
      style: "tableCell",
    }))
  );

  return {
    table: {
      headerRows: 1,
      widths: table.header.map(() => "*"),
      body: [header, ...rows],
    },
    layout: TABLE_LAYOUT,
    margin: [0, 4, 0, 12],
  };
};
